import { Button, Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { Box, Flex } from "styled-system/jsx";
import { StripeCardElement, StripeSplitCardFields } from "widgets/booker/shared";
import {
  useFailedScheduledPayment,
  UseFailedScheduledPaymentProps,
} from "./useFailedScheduledPayment";
import { failedScheduledPaymentRecipe } from "./failedScheduledPayment.recipe";
import { formatPrice } from "shared/utils";
import { useEffect, useState } from "react";
import { Collapsible } from "shared/components/molecules";
import { AnimationDefinition } from "framer-motion";

type FailedScheduledPaymentProps = UseFailedScheduledPaymentProps & {
  stripeRegion?: "CA" | "US";
  /** Booker Payment Settings: use the split card form with an optional postal code. */
  optionalCardPostalCode?: boolean;
};

export const FailedScheduledPayment = ({
  failedScheduledPayment,
  stripeRegion = "CA",
  optionalCardPostalCode = false,
}: FailedScheduledPaymentProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.FAILED_SCHEDULED_PAYMENT",
  });

  const {
    isCardReady,
    paymentState,
    loading,
    handlePayBalance,
    onCardChange,
    onSplitCardChange,
  } = useFailedScheduledPayment({ failedScheduledPayment });

  const classes = failedScheduledPaymentRecipe({
    color: paymentState === "success" ? "success" : "warning",
  });

  // The ScheduledPaymentFindOneDocument result will re-run in the parent component
  // AND the CheckoutSummary component after a successful payment.
  // isOpen contols IF the collapsible is open or closed.
  // isClosing is set to true right AFTER a successful payment,
  // and is used to ensure the element is not removed from the DOM while the success state is visible.
  // isHiddenAfterSuccess is set to true after the success state has been visible for 2 seconds
  // and the collapsible is closed. At this point, the element is removed from the DOM.
  const [isOpen, setIsOpen] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [isHiddenAfterSuccess, setIsHiddenAfterSuccess] = useState(false);
  useEffect(() => {
    setIsOpen(true);
    setIsHiddenAfterSuccess(false);
    if (paymentState === "success") {
      setIsClosing(true);
      const timeout = setTimeout(() => {
        setIsOpen(false);
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [paymentState]);

  if (isHiddenAfterSuccess || (!failedScheduledPayment && !isClosing)) {
    return null;
  }

  return (
    <Collapsible
      open={isOpen}
      onAnimationComplete={(definition: AnimationDefinition) => {
        if (definition === "closed") {
          setIsHiddenAfterSuccess(true);
          setIsClosing(false);
        }
      }}
    >
      <Flex className={classes.container}>
        <Flex flexDir="column" gap="100">
          <Text visual="bodySemiBold" className={classes.text}>
            {t("TITLE")}
          </Text>
          <Text visual="bodyRegular" className={classes.text}>
            {t("DESCRIPTION", {
              amount: formatPrice(failedScheduledPayment?.amount ?? 0),
            })}
          </Text>
        </Flex>

        <Flex flexDir="column" gap="75" w="full">
          <Flex
            alignItems={optionalCardPostalCode ? "flex-start" : "center"}
            flexDir={optionalCardPostalCode ? "column" : "row"}
            gap={optionalCardPostalCode ? "150" : "200"}
            w="full"
          >
            {optionalCardPostalCode ? (
              <StripeSplitCardFields
                onChange={onSplitCardChange}
                stripeRegion={stripeRegion}
                requirePostalCode={false}
                labels={{
                  cardNumber: t("CARD_NUMBER"),
                  expiry: t("EXPIRY"),
                  cvc: t("CVC"),
                  postalCode: t("POSTAL_CODE"),
                  postalPlaceholder: t("POSTAL_CODE_PLACEHOLDER"),
                  zipCode: t("ZIP_CODE"),
                  zipPlaceholder: t("ZIP_CODE_PLACEHOLDER"),
                }}
              />
            ) : (
              <StripeCardElement onChange={onCardChange} />
            )}
            {paymentState !== "success" ? (
              <Button
                visual="warning"
                disabled={!isCardReady}
                isLoading={loading}
                type="button"
                onClick={handlePayBalance}
                flexShrink={0}
              >
                {paymentState === "idle" || paymentState === "error"
                  ? t("CTA_BUTTONS.PAY_BALANCE")
                  : t("CTA_BUTTONS.PROCESSING")}
              </Button>
            ) : (
              <Box
                py="9px"
                px="200"
                borderRadius="100"
                bgColor="bg.success.dark"
                flexShrink={0}
              >
                <Text visual="bodyRegular" color="white">
                  {t("CTA_BUTTONS.SUCCESS")}
                </Text>
              </Box>
            )}
          </Flex>

          {paymentState === "error" && (
            <Text visual="smallMedium" color="text.warning.mid">
              {t("FAILED_TO_PROCESS")}
            </Text>
          )}
        </Flex>
      </Flex>
    </Collapsible>
  );
};
